import React, { createContext, useContext, useMemo, useEffect, ReactNode } from 'react';
import LoadingFallback from '../components/LoadingFallback';
import ErrorFallback from '../components/ErrorFallback';
import { useActiveCampaign } from '../hooks/useActiveCampaign';
import { trackEvent } from '../lib/analytics';

// Fail-closed: gates child rendering until an active campaign resolves, so every
// useCampaign() consumer gets a guaranteed campaignId instead of its own undefined guard.

interface CampaignContextValue {
  campaignId: string;
}

const CampaignContext = createContext<CampaignContextValue | undefined>(undefined);

export function CampaignProvider({ children }: { children: ReactNode }) {
  const { data: activeCampaign, isLoading, isError, error, refetch, isFetching } = useActiveCampaign();
  const campaignId = activeCampaign?.documentId;
  const value = useMemo(() => (campaignId ? { campaignId } : undefined), [campaignId]);

  const hasError = isError || (!isLoading && !campaignId);
  const errorMessage = isError ? (error?.message ?? 'Unknown error') : 'Active campaign response missing documentId';

  useEffect(() => {
    if (!hasError) return;
    if (process.env.NODE_ENV !== 'production') {
      console.error('CampaignProvider: failed to resolve active campaign', errorMessage);
    }
    trackEvent('campaign_fetch_error', { message: errorMessage });
  }, [hasError, errorMessage]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (hasError) {
    return (
      <ErrorFallback
        message="Error loading campaign. Please try again."
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    );
  }

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
}

/** Returns the guaranteed-resolved active campaign id. Never undefined — throws outside a CampaignProvider. */
export function useCampaign(): CampaignContextValue {
  const ctx = useContext(CampaignContext);
  if (!ctx) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return ctx;
}
